import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ChartFragment } from 'models/api-response';
import { DosageTime, MarRecord, Patient, SaveOptions, SimChartAssignment } from 'models/ui';
import { NAV_ID } from 'constants/app.constant';
import { compose } from 'redux';
import { appSelectors } from 'redux/ducks/app';
import { studentSelectors } from 'redux/ducks/student';
import Box from 'components/common/box/Box';
import Sidebar from 'components/common/sidebar/Sidebar';
import { FORM_FIELD_DEFAULT_VALUE, PATIENT_ID_MESSAGE, TIME_OFFSET_REGEX } from './constants';
import MarAdministerStep1 from './MarAdministerStep1';
import MarAdministerStep2 from './MarAdministerStep2';
import { buildAdministerFragments } from './helper';
import './mar.administer.sidebar.scss';

interface MarAdministerSidebarProps {
  administerList: MarRecord[];
  isOpen: boolean;
  onCloseSidebar: Function;
  fullAssignment: SimChartAssignment;
  fragments: ChartFragment[];
  patient: Patient;
  presaveFragment: Function;
  onUpdate: Function;
  saveChart: <T>(fragments: Array<Partial<ChartFragment>>, options?: SaveOptions) => Promise<T>;
  isAuthor?: boolean;
}

export type FieldType = {
  value?: string;
  label?: string;
  isRequired: boolean;
  error?: string;
  isTouched?: boolean;
  type?: string;
};

export type MarAdministerFormField = {
  id: string;
  label: string;
  value: string;
  drugName?: string;
  route?: string;
  dose?: string;
  frequency?: string;
  isRequired: boolean;
  error?: string;
  checked?: boolean;
  isTouched?: boolean;
  dosageTimeTable?: string;
  skipBarCode?: boolean;
  type?: string;
  dosageTimes?: DosageTime[];
  specialInstructions?: string;
  checkedMedication?: boolean;
  administrationTime?: FieldType;
  initials?: FieldType;
  comment?: FieldType;
  edit?: boolean;
  addedMedication?: boolean;
  medicationChanged?: boolean;
  dosageTimeSeparate?: string;
  originDosageTimes?: DosageTime[];
  fragmentId?: string;
  orderStartTime?: string;
};

const BARCODE_UNENABLE_MESSAGE = 'Select the medications you want to administer.';
const PATIENT_ID_DEFAULT_VALUE = {
  id: 'patientID',
  label: 'Scan Patient ID',
  value: '',
  isRequired: true,
  error: '',
  checked: false,
  isTouched: false,
  skipBarCode: false,
  type: 'text'
};

const MarAdministerSidebar = ({
  administerList,
  isOpen,
  onCloseSidebar,
  fullAssignment,
  fragments,
  patient,
  presaveFragment,
  onUpdate,
  saveChart,
  isAuthor
}: MarAdministerSidebarProps) => {
  const [step, setStep] = useState(1);
  const [patientIDField, setPatientIDField] = useState<MarAdministerFormField>({ ...PATIENT_ID_DEFAULT_VALUE });
  const [formField, setFormField] = useState<MarAdministerFormField[]>([]);
  const remapAdministerListToFormField = () => {
    const newMap = [];
    administerList.forEach((record) => {
      return newMap.push({
        ...record,
        id: record.fragmentId,
        label: 'Scan Barcode',
        dosageTimeTable: record.dosageTimeTable,
        drugName: record.drugName,
        orderStartTime: record.orderStartTime,
        route: record.route,
        dose: record.dose,
        frequency: record.frequency,
        checked: administerList.length === 1,
        ...FORM_FIELD_DEFAULT_VALUE
      });
    });
    setFormField([...newMap]);
  };

  const updateFormField = (record, getUpdatedField) => {
    const updatedFormFieldList = formField.map((field) => {
      if (field.id === record.id && field.dosageTimeTable === record.dosageTimeTable && field.dosageTimeSeparate === record.dosageTimeSeparate) {
        return getUpdatedField(field);
      }
      return field;
    });
    setFormField(updatedFormFieldList);
  };

  const onChange = (e, record) => {
    updateFormField(record, (field) => ({ ...field, value: e.target.value }));
  };

  const validateBarCode = (value, id) => {
    const messageBarcode = 'Barcode error. Ensure medication matches Patient ID';
    const emptyMessage = !value.trim() ? 'Scan or enter a barcode.' : '';
    if (emptyMessage) return emptyMessage;

    const barcode = fragments.find((fragment) => fragment.fragmentId === id)?.chartData?.barcode;
    return value !== barcode ? messageBarcode : '';
  };

  const validatePatientID = (value) => {
    const messagePatientID = 'Patient ID and barcode do not match. Verify the information and try again.';
    const emptyMessage = !value.trim() ? 'Enter a Patient ID.' : '';
    if (emptyMessage) return emptyMessage;
    return patient?.patientId !== value ? messagePatientID : '';
  };

  const triggerValidation = (e, record) => {
    const error = validateBarCode(e.target.value, record.id);
    updateFormField(record, (field) => ({ ...field, value: e.target.value, error, isTouched: true }));
  };

  const onBlur = (e, record) => {
    triggerValidation(e, record);
  };

  const onCheckboxHandler = (e, record) => {
    const updatedFormFieldList = formField.map((field) => {
      if (field.id === record.id && field.dosageTimeTable === record.dosageTimeTable && field.dosageTimeSeparate === record.dosageTimeSeparate) {
        let error = '';
        if (field.skipBarCode) error = '';
        else {
          error = field.checked ? '' : validateBarCode(field.value, record.id);
        }
        return { ...field, error, checked: !field.checked, isTouched: !field.checked };
      }
      return field;
    });
    setFormField(updatedFormFieldList);
  };

  const checkDisabledButton = () => {
    const checkedFields = formField.filter((field) => field.checked);
    if (!fullAssignment?.barcodeEnabled) {
      return !checkedFields.length;
    }

    if (checkedFields.length === 0) {
      return true;
    }

    if (!patientIDField.skipBarCode && validatePatientID(patientIDField.value)) {
      return true;
    }

    return checkedFields.some((field) => !field.skipBarCode && Boolean(validateBarCode(field.value, field.id)));
  };

  useEffect(() => {
    remapAdministerListToFormField();
  }, [administerList]);

  const handleContinue = () => {
    setStep(2);
  };

  const messageBarcode = () => {
    return fullAssignment?.barcodeEnabled ? PATIENT_ID_MESSAGE : BARCODE_UNENABLE_MESSAGE;
  };

  const onSkipBarcodeAll = () => {
    setPatientIDField({ ...patientIDField, skipBarCode: !patientIDField.skipBarCode, error: '' });
  };
  const onSkipBarcode = (record) => {
    updateFormField(record, (field) => ({ ...field, value: '', error: '', skipBarCode: !field.skipBarCode, isTouched: false }));
  };

  const addMedication = (record) => {
    updateFormField(record, (field) => ({ ...field, addedMedication: true, edit: true, medicationChanged: true }));
  };
  const resetMedication = (record) => {
    updateFormField(record, (field) => ({
      ...field,
      administrationTime: {
        ...FORM_FIELD_DEFAULT_VALUE.administrationTime
      },
      initials: {
        ...FORM_FIELD_DEFAULT_VALUE.initials
      },
      comment: {
        ...FORM_FIELD_DEFAULT_VALUE.comment
      }
    }));
  };

  const groupByFragmentId = (orders) => {
    return orders.reduce((acc, order) => {
      const existing = acc.find((item) => item.fragmentId === order.fragmentId);
      const { administrationTime, initials, comment, dosageTimeTable, dosageTimeSeparate } = order;
      if (existing) {
        existing.dosageTimeArray.push({ administrationTime, initials, comment, dosageTimeTable, dosageTimeSeparate });
      } else {
        acc.push({
          ...order,
          dosageTimeArray: [{ administrationTime, initials, comment, dosageTimeTable, dosageTimeSeparate }]
        });
      }

      return acc;
    }, []);
  };

  const administer = () => {
    const arrayFiltered = formField.filter((field) => field.addedMedication);
    const renewArray = groupByFragmentId(arrayFiltered);
    const paramsMedication = buildAdministerFragments(renewArray, administerList, fragments, presaveFragment, isAuthor);
    saveChart([...paramsMedication], { navId: NAV_ID.PHARMACY, afterSave: onUpdate });
  };
  const editMedication = (record) => {
    updateFormField(record, (field) => ({ ...field, edit: !field.edit, addedMedication: false }));
  };

  const onCheckboxMedication = (record) => {
    updateFormField(record, (field) => ({ ...field, checkedMedication: !field.checkedMedication }));
  };
  const onAdministrationTimeChange = (e, record) => {
    const value = isAuthor ? e.target.value : e;
    updateFormField(record, (field) => ({ ...field, administrationTime: { ...field.administrationTime, value, isTouched: true } }));
  };

  const onAdministrationTimeBlur = (e, record) => {
    if (isAuthor) {
      const errorMessage = !e.target.value.trim() || !TIME_OFFSET_REGEX.test(e.target.value) ? 'Time Offset must have format HH:mm' : '';
      updateFormField(record, (field) => ({ ...field, administrationTime: { ...field.administrationTime, error: errorMessage, isTouched: true } }));
    }
  };

  const onInitialsChange = (e, record) => {
    updateFormField(record, (field) => ({ ...field, initials: { ...field.initials, value: e.target.value, isTouched: true } }));
  };

  const onInitialsBlur = (e, record) => {
    const emptyMessage = !e.target.value.trim() ? 'Enter Initials.' : '';
    updateFormField(record, (field) => ({ ...field, initials: { ...field.initials, error: emptyMessage, isTouched: true } }));
  };

  const onCommentChange = (e, record) => {
    updateFormField(record, (field) => ({ ...field, comment: { ...field.comment, value: e.target.value, isTouched: true } }));
  };

  useEffect(() => {
    return () => {
      if (isOpen) {
        setPatientIDField({ ...PATIENT_ID_DEFAULT_VALUE });
        setFormField([]);
        setStep(1);
      }
    };
  }, [isOpen]);

  const step1Props = {
    patientIDField,
    formField,
    fullAssignment,
    onChange,
    onBlur,
    onCheckboxHandler,
    onSkipBarcode,
    onSkipBarcodeAll,
    checkDisabledButton,
    setPatientIDField,
    handleContinue,
    messageBarcode,
    validatePatientID
  };

  const step2Props = {
    formField: formField.filter((field) => field.checked),
    addMedication,
    resetMedication,
    administer,
    editMedication,
    onCheckboxMedication,
    onAdministrationTimeChange,
    onAdministrationTimeBlur,
    onInitialsChange,
    onInitialsBlur,
    onCommentChange,
    barcodeEnabled: fullAssignment?.barcodeEnabled,
    isAuthor
  };

  return (
    <Sidebar className="mar-administer-sidebar-box" open={isOpen} onClose={onCloseSidebar}>
      <Box mt3 mb2>
        <h2>Administer</h2>
      </Box>
      <Box className="mar-administer-sidebar">
        {step === 1 && <MarAdministerStep1 {...step1Props} />}
        {step === 2 && <MarAdministerStep2 {...step2Props} />}
      </Box>
    </Sidebar>
  );
};

const mapStateToProps = (state) => ({
  fullAssignment: studentSelectors.getFullAssignment(state),
  patient: appSelectors.getPatient(state)
});

export default compose(connect(mapStateToProps))(MarAdministerSidebar);
